// Generate a UUID-v4-shaped random string usable as a React list key or any
// non-security-sensitive client-side identifier.
//
// Why not just `crypto.randomUUID()`? Browsers gate that API to *secure
// contexts* — HTTPS pages and `http://localhost`/`http://127.0.0.1` only.
// Dev builds accessed over a LAN IP (`http://192.168.x.x:5173`, common when
// QA-ing on a phone) hit `crypto.randomUUID is not a function` because
// `window.crypto` exists but the method is undefined.
//
// `crypto.getRandomValues()` is NOT gated and works in every context, so we
// use it to assemble a UUID v4 manually when randomUUID isn't available.
// Final fallback to `Math.random()` covers stripped-down environments.

export function randomId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        // RFC 4122 §4.4: set version (4) and variant (10xx) bits.
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    // Last-resort pseudo-random fallback. Sufficient for React keys but never
    // use for anything where collisions or predictability matter.
    return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
